import { Box, Button, Typography } from "@mui/material";
import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import AppButton from "../../components/app-button/AppButton";
import Iconify from "../../components/iconify/Iconify";
import { KOT_STATUS, ORDER_TYPES_VALUES } from "../../constants/enums";
import { getAllKots, updateKotStatus } from "../../reduxStore/slices/kotSlice";
import { useNotify } from "../../utils/snackbarUtils";
import CancelKOTConfirmDialog from "./CancelKOTDialog";
import EmptyKOTState from "./EmptyKOTState";
import KOTCard from "./KOTCard";
import "./KOTView.css";
import KotHistoryDrawer from "./KotHistoryDrawer";

function KOTView(props) {
  const { showSuccess, handleApiError, showError } = useNotify();
  const dispatch = useDispatch();
  const { user } = useSelector((state) => state.auth);
  const { kotList, loading: isLoading } = useSelector((state) => state.kot);
  const storeId = user?.storeId;
  const [isShowCancelKOTDialog, setIsShowCancelKOTDialog] = useState(false);
  const [isHistoryOpen, setIsHistoryOpen] = useState(false);
  const [loadingIds, setLoadingIds] = useState({});
  const [selectedKOT, setSelectedKOT] = useState({});
  const [now, setNow] = useState(Date.now());

  useEffect(() => {
    const interval = setInterval(() => {
      setNow(Date.now());
    }, 1000);

    return () => clearInterval(interval);
  }, []);

  const fetchAllKots = async () => {
    try {
      const obj = {
        offset: 0,
        limit: 10,
        storeId: storeId,
      };
      await dispatch(getAllKots(obj)).unwrap();
    } catch (err) {
      handleApiError(err);
    }
  };

  useEffect(() => {
    if (storeId) {
      fetchAllKots();
    }
  }, [storeId]);

  const handleCancelKOT = (kotData) => {
    setSelectedKOT(kotData);
    setIsShowCancelKOTDialog(true);
  };

  const handleFoodReady = async (kotData) => {
    const id = kotData.order_kot_uid;

    setLoadingIds((prev) => ({
      ...prev,
      [id]: true,
    }));

    try {
      const obj = {
        store_id: storeId,
        order_uid: kotData?.order_uid,
        kot_comment: "",
        order_kot_id: kotData?.order_kot_id,
        kot_status_id: KOT_STATUS.READY,
        order_kot_uid: kotData?.order_kot_uid,
      };
      const res = await dispatch(updateKotStatus(obj)).unwrap();
      showSuccess("KOT marked as ready");
    } catch (err) {
      handleApiError(err);
    } finally {
      setLoadingIds((prev) => ({
        ...prev,
        [id]: false,
      }));
    }
  };

  const handleCancelOrderKOT = async (reason) => {
    const id = selectedKOT.order_kot_uid;
    setLoadingIds((prev) => ({
      ...prev,
      [id]: true,
    }));

    try {
      const obj = {
        store_id: storeId,
        order_uid: selectedKOT?.order_uid,
        kot_comment: reason,
        order_kot_id: selectedKOT?.order_kot_id,
        kot_status_id: KOT_STATUS.CANCELLED,
        order_kot_uid: selectedKOT?.order_kot_uid,
      };
      const res = await dispatch(updateKotStatus(obj)).unwrap();
      showSuccess("KOT cancelled successfully");
      setIsShowCancelKOTDialog(false);
    } catch (err) {
      handleApiError(err);
      setIsShowCancelKOTDialog(false);
    } finally {
      setLoadingIds((prev) => ({
        ...prev,
        [id]: false,
      }));
    }
  };

  return (
    <>
      <Box p={3}>
        <Box
          display="flex"
          alignItems="center"
          justifyContent="space-between"
          mb={1}
        >
          <Typography variant="h5" fontWeight="bold">
            KOT View
          </Typography>
          <Box display="flex" alignItems="center" gap={1}>
            {kotList.length !== 0 && !isLoading && (
              <Box
                style={{
                  display: "flex",
                  gap: "10px",
                }}
              >
                <AppButton
                  style={{
                    background: "#e0a800",
                  }}
                  variant="contained"
                  startIcon={<Iconify icon="solar:refresh-bold-duotone" />}
                  onClick={fetchAllKots}
                  loading={isLoading}
                >
                  Refresh
                </AppButton>
              </Box>
            )}
            <Button variant="outlined" onClick={() => setIsHistoryOpen(true)}>
              History
            </Button>
          </Box>
        </Box>
        <Box
          sx={{
            mt: 2,
            display: "flex",
            justifyContent: "space-between",
            gap: "15px",
            fontSize: "14px",
          }}
        >
          <Box>Active (20)</Box>
          <Box
            sx={{
              mt: 2,
              display: "flex",
              justifyContent: "flex-end",
              gap: "15px",
              fontSize: "14px",
            }}
          >
            {ORDER_TYPES_VALUES.map((order) => {
              return (
                <Box className="table-status-content" key={order.value}>
                  <Box
                    className="table-status-ico"
                    style={{ background: order.bgColor }}
                  />
                  <Box className="table-status-name">{order.label}</Box>
                </Box>
              );
            })}
          </Box>
        </Box>

        {/* Grid of KOT cards */}
        {kotList.length === 0 && !isLoading ? (
          <EmptyKOTState onRefresh={fetchAllKots} loading={isLoading} />
        ) : (
          <Box
            display="grid"
            gridTemplateColumns="repeat(auto-fill, minmax(300px, 1fr))"
            gap={2}
            sx={{
              mt: 3,
            }}
          >
            {kotList.map((kot, idx) => (
              <KOTCard
                key={kot.order?.kots[0]?.order_kot_uid}
                orderData={{ ...kot.order, quantity_delta: 1 }}
                now={now}
                onCancelKOTClick={handleCancelKOT}
                onFoodReadyClick={handleFoodReady}
                loadingIds={loadingIds}
              />
            ))}
          </Box>
        )}
      </Box>

      <CancelKOTConfirmDialog
        open={isShowCancelKOTDialog}
        onClose={() => setIsShowCancelKOTDialog(false)}
        onCancel={handleCancelOrderKOT}
      />

      <KotHistoryDrawer
        open={isHistoryOpen}
        onClose={() => setIsHistoryOpen(false)}
      />
    </>
  );
}

export default KOTView;
